import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Delete } from "lucide-react";
import { api } from "../lib/api";
import { apiError, useAuth } from "../lib/auth";
import { AuthCard, Field, FormError, SubmitBtn } from "../components/auth/AuthCard";

const DEVICE = "aegis-device";

const googleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const GoogleBtn = () => (
  <button type="button" onClick={googleLogin} className="w-full flex items-center justify-center gap-3 py-3 border border-white/20 font-mono text-xs tracking-[0.15em] hover:border-brand transition-colors" data-testid="google-login-btn">
    <svg viewBox="0 0 48 48" className="w-4 h-4"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
    CONTINUE WITH GOOGLE
  </button>
);

const PinPad = ({ info, onDone, onSwitch }) => {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const press = (d) => { if (busy) return; setError(""); setPin((p) => (p.length < 6 ? p + d : p)); };
  useEffect(() => {
    const key = (e) => { if (/^\d$/.test(e.key)) press(e.key); if (e.key === "Backspace") setPin((p) => p.slice(0, -1)); };
    window.addEventListener("keydown", key); return () => window.removeEventListener("keydown", key);
  });
  useEffect(() => {
    if (pin.length !== 6) return;
    setBusy(true);
    api.post("/auth/pin", { device_token: localStorage.getItem(DEVICE), pin })
      .then((r) => onDone(r.data)).catch((e) => { setError(apiError(e)); setPin(""); }).finally(() => setBusy(false));
  }, [pin, onDone]);
  return (
    <div data-testid="pin-pad">
      <FormError error={error} />
      <div className="flex justify-center gap-3 mb-6" data-testid="pin-dots">
        {[0, 1, 2, 3, 4, 5].map((i) => <span key={i} className={`w-3.5 h-3.5 rounded-full border transition-colors ${i < pin.length ? "bg-brand border-brand" : "border-white/30"}`} />)}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "del"].map((k) => k === "" ? <span key="x" /> : (
          <button key={k} type="button" onClick={() => (k === "del" ? setPin((p) => p.slice(0, -1)) : press(k))} disabled={busy}
            className="h-14 border border-white/10 font-mono text-xl hover:border-brand hover:text-brand active:scale-95 transition-[transform,color,border-color] disabled:opacity-40" data-testid={`pin-key-${k}`}>
            {k === "del" ? <Delete className="w-5 h-5 mx-auto" /> : k}
          </button>))}
      </div>
      <button onClick={onSwitch} className="mt-6 w-full font-mono text-[11px] text-white/50 hover:text-white transition-colors" data-testid="use-password-btn">Use password or Google instead</button>
    </div>
  );
};

export function AuthCallback() {
  const { hash } = useLocation();
  const { signIn } = useAuth();
  const nav = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(hash.slice(1)).get("session_id");
    api.post("/auth/google", { session_id: sid })
      .then((r) => { signIn(r.data); nav("/app", { replace: true }); })
      .catch((e) => nav(`/login?error=${encodeURIComponent(apiError(e))}`, { replace: true }));
  }, [hash, signIn, nav]);
  return <div className="min-h-screen bg-canvas grid place-items-center font-mono text-xs text-white/40" data-testid="auth-callback">SIGNING YOU IN WITH GOOGLE…</div>;
}

export default function Login() {
  const { user, signIn } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(params.get("error") || "");
  const [forgot, setForgot] = useState(false);
  const [device, setDevice] = useState(null);
  const [mode, setMode] = useState(localStorage.getItem(DEVICE) ? "checking" : "password");
  const next = params.get("next") || "/app";
  useEffect(() => {
    const t = localStorage.getItem(DEVICE);
    if (!t) return;
    api.post("/auth/device", { device_token: t }).then((r) => { setDevice(r.data); setMode(r.data.has_pin ? "pin" : "password"); })
      .catch(() => { localStorage.removeItem(DEVICE); setMode("password"); });
  }, []);
  if (user) return <Navigate to={next} replace />;
  const finish = (data) => { signIn(data); nav(next, { replace: true }); };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      if (forgot) { await api.post("/auth/forgot-password", { email: form.email }); setForgot("sent"); }
      else { const { data } = await api.post("/auth/login", form); finish(data); }
    } catch (err) { setError(apiError(err)); }
    setBusy(false);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (mode === "checking") return <AuthCard testid="login-page" title="Sign in" subtitle="Checking this device…" />;
  if (mode === "pin") {
    return (
      <AuthCard testid="login-page" title={`Welcome back${device?.name ? `, ${device.name}` : ""}`} subtitle={`Enter your 6-digit PIN · ${device?.email_hint}`}>
        <PinPad info={device} onDone={finish} onSwitch={() => setMode("password")} />
      </AuthCard>
    );
  }
  return (
    <AuthCard testid="login-page" title={forgot ? "Reset password" : "Sign in"}
      subtitle={forgot ? "We'll generate a secure reset link for your account." : "Access your AEGIS Guard defense workspace."}>
      {!forgot && <><GoogleBtn /><p className="text-center font-mono text-[10px] text-white/30 my-4">OR</p></>}
      <form onSubmit={submit}>
        <FormError error={error} />
        {forgot === "sent" && <p className="text-sm text-brand mb-4" data-testid="forgot-sent">If that account exists, a reset link has been issued. Ask your workspace owner if you don't receive it.</p>}
        <Field label="Email" type="email" required autoComplete="email" value={form.email} onChange={set("email")} testid="login-email-input" />
        {!forgot && <Field label="Password" type="password" required autoComplete="current-password" value={form.password} onChange={set("password")} testid="login-password-input" />}
        <SubmitBtn busy={busy} testid="login-submit-btn">{forgot ? "SEND RESET LINK" : "SIGN IN"}</SubmitBtn>
      </form>
      <div className="flex justify-between mt-4">
        <button onClick={() => { setForgot(!forgot); setError(""); }} className="font-mono text-[11px] text-white/45 hover:text-white transition-colors" data-testid="toggle-forgot-btn">{forgot ? "← Back to sign in" : "Forgot password?"}</button>
        {device?.has_pin && <button onClick={() => setMode("pin")} className="font-mono text-[11px] text-brand hover:underline" data-testid="use-pin-btn">Use PIN</button>}
      </div>
      <p className="mt-6 text-[10px] leading-relaxed text-white/30" data-testid="login-analytics-notice">AEGIS collects product usage analytics (pages visited, actions, errors and MARI questions) to improve the product.</p>
    </AuthCard>
  );
}
